import React, { useEffect, useState } from 'react';
import { 
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Pagination,
  Chip,
  Tooltip,
  IconButton,
  useTheme,
  Modal,
  Button,
  Divider,
  Skeleton
} from '@mui/material';
import { FaSyncAlt as RefreshIcon, FaInfoCircle as InfoIcon } from 'react-icons/fa';
import { getData } from '../../Webservice/Webservice';

const PAGE_SIZE = 20;

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const theme = useTheme();
  const handleOpenModal = (details) => {
    setSelectedDetails(details);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDetails(null);
  };

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const res = await getData('logs/activity', { page, limit: PAGE_SIZE }, token, false);
      setLogs(res.logs || []);
      setMeta(res.meta || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      setError('Failed to fetch activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page]);

  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'create': return 'success';
      case 'update': return 'info';
      case 'delete': return 'error';
      default: return 'primary';
    }
  };

  const getActionStyles = (action) => {
    switch (action.toLowerCase()) {
      case 'login success':
        return { backgroundColor: '#2e7d32', color: '#fff' }; // Green
      case 'failed login':
        return { backgroundColor: '#f44336', color: '#fff' }; // Red
      case 'logout':
        return { backgroundColor: '#2196f3', color: '#fff' }; // Blue
      case 'create':
        return { backgroundColor: '#00bcd4', color: '#fff' }; // Cyan
      case 'update':
        return { backgroundColor: '#ff9800', color: '#fff' }; // Orange
      case 'delete':
        return { backgroundColor: '#9c27b0', color: '#fff' }; // Purple
      default:
        return { backgroundColor: '#607d8b', color: '#fff' }; // Grey
    }
  };

  const formatUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown';
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    
    return userAgent.substring(0, 30) + (userAgent.length > 30 ? '...' : '');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h3" component="h1" sx={{
                      color: '#1a202c',
                      fontFamily:'Mulish, sans-serif'
                  }}>
            Activity Log
          </Typography>
          <Chip
            label={`${meta.total || logs.length} ${meta.total === 1 ? 'Entry' : 'Entries'}`}
            size="small"
            sx={{ 
                backgroundColor: 'rgba(255, 209, 2, 0.2)', 
                color: '#b39200',
                fontFamily: 'Mulish, sans-serif', 
                fontWeight: '800',
                border: '1px solid rgba(255, 209, 2, 0.5)'
            }}
          />
        </Box>
        <Box>
          <Tooltip title="Refresh Logs" arrow>
            <IconButton 
              onClick={() => fetchLogs(meta.page)} 
              disabled={loading}
              sx={{
                color: '#4b5563',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                '&:hover': { 
                  backgroundColor: 'rgb(255 209 2 / 20%)',
                  color: '#b39200'
                },
                borderRadius: '10px',
                transition: 'all 0.2s ease'
              }}
            >
              <RefreshIcon size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        border:'1px solid #d3d3d3de',
                                    }}>
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="activity log table">
          <TableHead>
            <TableRow sx={{ 
              '& th': {
                backgroundColor: '#f8f9fa',
                fontWeight: 800,
                py: 2,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#5c636a',
                fontFamily: 'Mulish, sans-serif',
                borderBottom: '2px solid #edeff1',
              }
            }}>
              <TableCell>Date & Time</TableCell>
              <TableCell>User Info</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Network Info</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton width={140} />
                    <Skeleton width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={160} />
                    <Skeleton width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={100} height={28} />
                  </TableCell>
                    <TableCell>
                      <Skeleton width={140} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={160} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                </TableRow>
              ))
            )}
            
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <img 
                      src="./img/nodata.png" 
                      alt="No logs" 
                      style={{ width: '180px' }} 
                    />  
                    <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif', color: 'text.secondary' }}>
                      No activity logs found.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!loading && logs.map((log) => (
              <TableRow 
                key={log._id} 
                hover
                sx={{ 
                  '&:hover': { backgroundColor: '#f9fafb' },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell>
                  <Typography 
                    fontWeight={700} 
                    sx={{ 
                      fontFamily: 'Mulish, sans-serif', 
                      fontSize: '0.9rem',
                      color: '#1a1c1e'
                    }}
                  >
                    {new Date(log.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{fontFamily:'Mulish, sans-serif', color: '#9ca3af', display: 'block'}}
                  >
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    fontWeight={600} 
                    sx={{fontFamily:'Mulish, sans-serif', color: '#374151', fontSize: '0.85rem'}}
                  >
                    {log.userId ? `${log.userId.firstName} ${log.userId.lastName}` : 'System'}
                  </Typography>
                  {log.userId && (
                    <Typography 
                      variant="caption" 
                      sx={{fontFamily:'Mulish, sans-serif', color: '#9ca3af', display: 'block'}}
                    >
                      {log.userId.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={log.action} 
                    size="small"
                    sx={{
                      fontFamily:'Mulish, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      borderRadius: '6px',
                      ...getActionStyles(log.action),
                      border: 'none',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography 
                    fontWeight={600} 
                    sx={{fontFamily:'Mulish, sans-serif', color: '#4b5563', fontSize: '0.85rem'}}
                  >
                    {log.entity}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View JSON Details" arrow>
                    <Button
                      size="small"
                      startIcon={<InfoIcon size={14} />}
                      onClick={() => handleOpenModal(log.details)}
                      sx={{
                        fontFamily: 'Mulish, sans-serif',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        px: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Details
                    </Button>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography 
                    fontWeight={500} 
                    sx={{fontFamily:'Mulish, sans-serif', color: '#4b5563', fontSize: '0.85rem'}}
                  >
                    {log.ipAddress}
                  </Typography>
                  <Tooltip title={log.userAgent} arrow>
                    <Typography 
                      variant="caption" 
                      sx={{fontFamily:'Mulish, sans-serif', color: '#9ca3af', display: 'block'}}
                    >
                      {formatUserAgent(log.userAgent)}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3,
        '& .MuiPagination-ul': {
          flexWrap: 'nowrap'
        }
      }}>
        <Pagination
          count={meta.totalPages}
          page={meta.page}
          onChange={handlePageChange}
          sx={{
            '& .MuiPagination-ul': { flexWrap: 'nowrap' },
            '& .MuiPaginationItem-root': {
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 600,
                borderRadius: '8px',
                '&.Mui-selected': {
                    backgroundColor: 'rgb(255, 209, 2)',
                    color: 'black',
                    '&:hover': {
                        backgroundColor: 'rgb(255, 190, 0)',
                    }
                }
            }
          }}
          shape="rounded"
          disabled={loading}
          showFirstButton
          showLastButton
        />
      </Box>

      {meta.total > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
        </Typography>
      )}
      {/* Modal for details */}
       <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="details-modal-title"
        aria-describedby="details-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          p: 4,
          borderRadius: 3,
          minWidth: 400,
          outline: 'none' 
        }}>
          <Typography id="details-modal-title" variant="h5" sx={{ mb: 2, fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#1a202c' }}>
            Activity Details
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box id="details-modal-description" sx={{ maxHeight: 450, overflowY: 'auto' }}>
            {selectedDetails ? (
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                {Object.entries(selectedDetails).map(([key, value], index, array) => (
                  <Box 
                    key={key} 
                    sx={{ 
                      display: 'flex', 
                      p: 2, 
                      borderBottom: index === array.length - 1 ? 'none' : '1px solid #e2e8f0',
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#475569', 
                        width: '140px', 
                        flexShrink: 0,
                        fontFamily: 'Mulish, sans-serif',
                        textTransform: 'capitalize'
                      }}
                    >
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#1e293b', 
                        fontFamily: 'Mulish, sans-serif',
                        wordBreak: 'break-all',
                        fontWeight: 500
                      }}
                    >
                      {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="textSecondary" sx={{ fontFamily: 'Mulish, sans-serif', textAlign: 'center', py: 4 }}>
                No details available.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button 
                onClick={handleCloseModal} 
                variant="contained" 
                sx={{ 
                    fontFamily: 'Mulish, sans-serif',
                    bgcolor: 'rgb(255, 209, 2)', 
                    color: 'black', 
                    fontWeight: 700,
                    borderRadius: '10px',
                    '&:hover': { bgcolor: 'rgb(255, 190, 0)' } 
                }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ActivityLog;